import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { currentPeriod, formatPeriod } from '@trestly/shared';
import { useSettings } from './api/hooks';
import { ErrorState } from './components/EmptyState';
import { Spinner } from './components/Spinner';
import { errorText } from './lib/errors';

interface PeriodContextValue {
  period: string;
  /** null — «вернуться к текущему периоду»: выбранный период не запоминаем. */
  setPeriod: (period: string | null) => void;
  /** Период «сейчас» — для подсветки и для дефолтной даты траты. */
  todayPeriod: string;
  /** День начала цикла (1..31) — из него считаются границы периода. */
  cycleStartDay: number;
}

const PeriodContext = createContext<PeriodContextValue | null>(null);

export function PeriodProvider({ children }: { children: ReactNode }): JSX.Element {
  // День начала цикла — настройка: период считаем в Europe/Minsk (на сервере в UTC
  // граница переключалась бы не тогда) и по циклу, а не по календарному месяцу.
  const settings = useSettings();
  const cycleStartDay = settings.data?.cycleStartDay ?? 1;

  const todayPeriod = useMemo(
    () => formatPeriod(currentPeriod('Europe/Minsk', new Date(), cycleStartDay)),
    [cycleStartDay],
  );

  // null = показываем текущий период. Со сменой дня цикла в настройках период
  // пересчитается сам, и пользователь не застрянет на окне со старыми границами.
  const [selected, setSelected] = useState<string | null>(null);
  const period = selected ?? todayPeriod;

  const value = useMemo<PeriodContextValue>(
    () => ({ period, setPeriod: setSelected, todayPeriod, cycleStartDay }),
    [period, todayPeriod, cycleStartDay],
  );

  // Ждём настройки: без дня цикла сегодняшний период посчитался бы по календарю
  // и экраны успели бы запросить не тот период.
  if (settings.isPending) return <Spinner block />;
  if (settings.isError) {
    return (
      <ErrorState message={errorText(settings.error)} onRetry={() => void settings.refetch()} />
    );
  }

  return <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>;
}

export function usePeriod(): PeriodContextValue {
  const ctx = useContext(PeriodContext);
  if (!ctx) throw new Error('usePeriod must be used inside PeriodProvider');
  return ctx;
}
